/**
 * Comunicación con los consultorios — chat de dos paneles (CB-09).
 *
 * La estructura del chat vive acá: conversaciones a la izquierda, la
 * conversación abierta a la derecha ocupando todo el alto, redactor anclado
 * abajo. De la referencia de Rosetta se toma SOLO esa estructura y la etiqueta
 * de origen sobre cada burbuja (A-CB-09 bis: «ante la duda, menos») — nada de
 * filtros por canal, IA, adjuntos ni chips de calificación.
 *
 * 🔴 La **entrega** —que un mensaje llegue por WhatsApp o SMS— NO está
 * conectada: un mensaje queda guardado y se lee cuando el consultorio entra al
 * portal (ver `domain/messaging.ts`). Por eso la cabecera de la conversación
 * lleva la advertencia fija, y por eso NO hay indicadores de enviado /
 * recibido / leído: dibujarlos sería mentir.
 *
 * ── El mapeo hilos → conversación (decisión de implementación) ─────────────
 * El dueño pidió «una conversación por doctor, como WhatsApp» (P6.1 FINAL),
 * pero el modelo guarda hilos por consultorio (`message_threads`, con
 * `subject` y `party_id` opcional). El mapeo elegido:
 *
 *   · Los hilos se AGRUPAN por interlocutor: `party_id` cuando el hilo es de
 *     un doctor, y el consultorio (`service_unit_id`) cuando no tiene doctor
 *     asignado. Un doctor = una conversación, con todos sus hilos adentro en
 *     orden cronológico; los hilos viejos por consultorio quedan como
 *     conversaciones del consultorio. Nada se borra ni se migra.
 *   · El «asunto» dejó de mostrarse en la lista (P6.3 FINAL: foto y nombre
 *     arriba, último mensaje abajo). En la conversación, cada hilo histórico
 *     conserva su asunto como separador discreto — el dato no se pierde en
 *     silencio, que es lo que P6.3 exige de la migración.
 *   · Responder escribe en el hilo abierto más reciente de la conversación;
 *     si no hay ninguno abierto, se crea un hilo nuevo cuyo `subject` se
 *     deriva de la primera línea del mensaje (el esquema lo exige `not null`;
 *     nadie vuelve a escribir asuntos a mano).
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { dateLabelLong } from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { haptic } from '@/lib/haptics'; // HAPTIC_FEEDBACK
import {
  Async, Avatar, Badge, Button, Empty, Field, Modal, Note,
} from '@/components/ui';
import '@/styles/chat.css';

/** Los timestamps de la base son ISO completos; `dateLabelLong` quiere solo la fecha. */
const dateOnly = (iso: string) => dateLabelLong(iso.slice(0, 10));

/* ── Tipos que llegan del Worker ──────────────────────────────────────────── */

interface Thread {
  id: string; subject: string; status: 'OPEN' | 'CLOSED';
  service_unit_id: string; party_id: string | null; party_profile_id: string | null;
  unit_code: string; unit_name: string | null; party_name: string | null;
  last_message_at: string; unread: number; last_body: string | null;
}

interface Message {
  id: string; body: string; is_internal: number; author_id: string | null;
  author_name: string | null; created_at: string;
}

interface ThreadDetail {
  thread: Thread & { organization_id: string };
  messages: Message[];
}

/* ── El agrupado hilos → conversación ─────────────────────────────────────── */

interface Conversation {
  key: string;
  /** El interlocutor: el doctor del hilo, o el consultorio si no hay doctor. */
  name: string;
  unitCode: string;
  unitId: string;
  /** Para la foto. Solo los doctores tienen; el consultorio cae a iniciales. */
  profileId: string | null;
  isDoctor: boolean;
  partyId: string | null;
  /** Ascendente por actividad: la conversación se lee de arriba hacia abajo. */
  threads: Thread[];
  lastAt: string;
  lastBody: string | null;
  unread: number;
  anyOpen: boolean;
}

function groupThreads(threads: Thread[]): Conversation[] {
  const byKey = new Map<string, Conversation>();

  for (const t of threads) {
    const key = t.party_id ?? `unit:${t.service_unit_id}`;
    let conv = byKey.get(key);
    if (!conv) {
      conv = {
        key,
        name: t.party_name ?? `Consultorio ${t.unit_code}`,
        unitCode: t.unit_code,
        unitId: t.service_unit_id,
        profileId: t.party_profile_id,
        isDoctor: t.party_id != null,
        partyId: t.party_id,
        threads: [],
        lastAt: t.last_message_at,
        lastBody: t.last_body,
        unread: 0,
        anyOpen: false,
      };
      byKey.set(key, conv);
    }
    conv.threads.push(t);
    conv.unread += t.unread;
    if (t.status === 'OPEN') conv.anyOpen = true;
  }

  for (const conv of byKey.values()) {
    conv.threads.sort((a, b) => a.last_message_at.localeCompare(b.last_message_at));
    const newest = conv.threads[conv.threads.length - 1]!;
    conv.lastAt = newest.last_message_at;
    conv.lastBody = newest.last_body;
  }

  return Array.from(byKey.values()).sort((a, b) => b.lastAt.localeCompare(a.lastAt));
}

/** El asunto que se deriva del primer mensaje de un hilo nuevo (P6.3: nadie
 *  vuelve a escribir asuntos; el esquema lo exige `not null`). */
function subjectFromBody(body: string): string {
  const firstLine = body.trim().split('\n')[0] ?? '';
  return firstLine.length > 80 ? `${firstLine.slice(0, 77)}…` : firstLine || 'Conversación';
}

/* ── La pantalla ──────────────────────────────────────────────────────────── */

export function MessagesPage() {
  const [statusFilter, setStatusFilter] = useState<'OPEN' | 'ALL'>('OPEN');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const params = statusFilter === 'OPEN' ? '?status=OPEN' : '';
  const threads = useApi<{ threads: Thread[] }>(`/admin/threads${params}`);

  const conversations = groupThreads(threads.data?.threads ?? []);
  const selected = conversations.find((conv) => conv.key === selectedKey) ?? null;

  return (
    <div className={`chat${selected ? ' chat--open' : ''}`}>
      {/* ── Panel izquierdo: las conversaciones ─────────────────────────── */}
      <aside className="chat__list">
        <header className="chat__list-head">
          <h1>Comunicación</h1>
          <div className="spacer" />
          <Button variant="primary" onClick={() => setCreating(true)}>Nueva</Button>
        </header>

        <div className="tabs chat__tabs">
          <button
            type="button"
            className={`tab${statusFilter === 'OPEN' ? ' is-active' : ''}`}
            aria-pressed={statusFilter === 'OPEN'}
            onClick={() => { if (statusFilter !== 'OPEN') { haptic.light(); setStatusFilter('OPEN'); } }} // HAPTIC_FEEDBACK
          >
            Abiertas
          </button>
          <button
            type="button"
            className={`tab${statusFilter === 'ALL' ? ' is-active' : ''}`}
            aria-pressed={statusFilter === 'ALL'}
            onClick={() => { if (statusFilter !== 'ALL') { haptic.light(); setStatusFilter('ALL'); } }} // HAPTIC_FEEDBACK
          >
            Todas
          </button>
        </div>

        <div className="chat__items">
          <Async query={threads} empty={(d) => d.threads.length === 0 && (
            <Empty
              icon="comunicacion"
              title="Sin conversaciones"
              hint="Empezá una con «Nueva»."
              action={<Button onClick={() => setCreating(true)}>Nueva conversación</Button>}
            />
          )}>
            {() => (
              <>
                {conversations.map((conv) => (
                  <ConversationItem
                    key={conv.key}
                    conv={conv}
                    active={conv.key === selectedKey}
                    onSelect={() => { haptic.light(); setSelectedKey(conv.key); }} // HAPTIC_FEEDBACK
                  />
                ))}
              </>
            )}
          </Async>
        </div>
      </aside>

      {/* ── Panel derecho: la conversación ──────────────────────────────── */}
      <section className="chat__pane" aria-label="Conversación">
        {selected ? (
          <ConversationPane
            key={selected.key}
            conv={selected}
            onBack={() => setSelectedKey(null)}
          />
        ) : (
          <div className="chat__empty">
            <Empty
              icon="comunicacion"
              title="Elegí una conversación"
              hint="Los mensajes no salen por WhatsApp ni SMS: se leen acá y en el portal del consultorio."
            />
          </div>
        )}
      </section>

      {creating && (
        <NewConversationModal
          onClose={() => setCreating(false)}
          onCreated={() => setCreating(false)}
        />
      )}
    </div>
  );
}

/* ── Un ítem de la lista ──────────────────────────────────────────────────── */

function ConversationItem({ conv, active, onSelect }: {
  conv: Conversation; active: boolean; onSelect: () => void;
}) {
  return (
    // Selección marcada con borde completo (el detalle de Rosetta que sí se
    // toma), no con relleno — y `aria-current` para que no sea solo visual.
    <button
      type="button"
      className={`chat-item${active ? ' is-active' : ''}`}
      aria-current={active}
      onClick={onSelect}
    >
      <Avatar
        name={conv.name}
        size={40}
        lazy
        src={conv.profileId ? `/api/admin/profiles/${conv.profileId}/photo` : null}
      />
      <span className="chat-item__body">
        <span className="chat-item__top">
          <span className="chat-item__name">{conv.name}</span>
          <time className="chat-item__date">{dateOnly(conv.lastAt)}</time>
        </span>
        <span className="chat-item__bottom">
          <span className="chat-item__preview">
            {conv.isDoctor && <span className="chat-item__unit">{conv.unitCode}</span>}
            {conv.lastBody ?? 'Sin mensajes'}
          </span>
          {conv.unread > 0 && (
            <span className="chat-item__unread" aria-label={`${conv.unread} sin leer`}>
              {conv.unread}
            </span>
          )}
          {!conv.anyOpen && <Badge tone="neutral" plain>Cerrada</Badge>}
        </span>
      </span>
    </button>
  );
}

/* ── La conversación abierta ──────────────────────────────────────────────── */

function ConversationPane({ conv, onBack }: { conv: Conversation; onBack: () => void }) {
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // El hilo abierto más reciente recibe la respuesta; sin uno abierto, el
  // redactor crea un hilo nuevo (ver el mapeo en la cabecera del archivo).
  const openThreads = conv.threads.filter((t) => t.status === 'OPEN');
  const replyTarget = openThreads[openThreads.length - 1] ?? null;

  const reply = useApiMutation(
    () => (replyTarget
      ? api.post(`/admin/threads/${replyTarget.id}/messages`, { body, is_internal: internal })
      : api.post<{ id: string }>('/admin/threads', {
        service_unit_id: conv.unitId,
        party_id: conv.partyId,
        subject: subjectFromBody(body),
        body,
      })),
    ['/admin/threads'],
    { onSuccess: () => { haptic.success(); setBody(''); setInternal(false); } }, // HAPTIC_FEEDBACK
  );

  const toggleStatus = useApiMutation(
    async () => {
      if (openThreads.length > 0) {
        // Cerrar la conversación cierra TODOS sus hilos abiertos: dejar uno a
        // medias haría que la conversación siga apareciendo como abierta.
        await Promise.all(openThreads.map((t) => api.post(`/admin/threads/${t.id}/close`)));
      } else {
        const newest = conv.threads[conv.threads.length - 1];
        if (newest) await api.post(`/admin/threads/${newest.id}/reopen`);
      }
    },
    ['/admin/threads'],
    { onSuccess: () => haptic.light() }, // HAPTIC_FEEDBACK
  );

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  return (
    <div className="chat-pane">
      <header className="chat-pane__head">
        <button type="button" className="btn btn--ghost chat-pane__back" onClick={onBack} aria-label="Volver a la lista">
          ←
        </button>
        <Avatar
          name={conv.name}
          size={34}
          src={conv.profileId ? `/api/admin/profiles/${conv.profileId}/photo` : null}
        />
        <div className="chat-pane__who">
          <div className="strong">{conv.name}</div>
          <div className="small muted">
            <Link to={`/admin/consultorios/${conv.unitId}`}>Consultorio {conv.unitCode}</Link>
            {!conv.anyOpen && ' · cerrada'}
          </div>
        </div>
        <div className="spacer" />
        <Button disabled={toggleStatus.isPending} onClick={() => toggleStatus.mutate()}>
          {conv.anyOpen ? 'Cerrar conversación' : 'Reabrir'}
        </Button>
      </header>

      {/* La advertencia de no-entrega SOBREVIVE al rediseño (CB-09, criterio
          7): banda fija en la cabecera, no un subtítulo que se pierde. */}
      <div className="chat-pane__warn" role="note">
        Estos mensajes <strong>no salen por WhatsApp ni SMS</strong> — el
        consultorio los lee cuando entra al portal.
      </div>

      <div className="chat-pane__scroll" ref={scrollRef}>
        {conv.threads.map((t, i) => (
          <ThreadMessages
            key={t.id}
            thread={t}
            showSubject={conv.threads.length > 1}
            isLast={i === conv.threads.length - 1}
            onLoaded={scrollToBottom}
          />
        ))}
      </div>

      {reply.error && <Note tone="danger">{reply.error.message}</Note>}

      <footer className="chat-pane__composer">
        <textarea
          className="input chat-pane__input"
          rows={2}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={internal ? 'Escribí la nota interna…' : 'Escribí el mensaje…'}
          aria-label={internal ? 'Nota interna' : 'Mensaje'}
        />
        <div className="chat-pane__actions">
          <label className={`chat-pane__internal${internal ? ' is-on' : ''}`}>
            <input
              type="checkbox"
              checked={internal}
              onChange={(e) => setInternal(e.target.checked)}
            />
            Nota interna — no la ve el consultorio
          </label>
          <div className="spacer" />
          <Button
            variant="primary"
            disabled={!body.trim() || reply.isPending}
            onClick={() => reply.mutate()}
          >
            {reply.isPending ? 'Enviando…' : internal ? 'Guardar nota' : 'Enviar'}
          </Button>
        </div>
      </footer>
    </div>
  );
}

/**
 * Los mensajes de un hilo, como burbujas.
 *
 * Cada hilo de la conversación trae los suyos por separado (el endpoint es por
 * hilo, y abrirlo marca como leído lo que escribió el otro). El lado de cada
 * burbuja: es DEL CLIENTE si no tiene autor o si el autor es el doctor de la
 * conversación; cualquier otro autor es del equipo. La regla es una heurística
 * de presentación — la **etiqueta de origen sobre cada burbuja** (el patrón de
 * Rosetta que sí se tomó) es lo que garantiza que la autoría nunca se pierda,
 * diga lo que diga el lado.
 */
function ThreadMessages({ thread, showSubject, isLast, onLoaded }: {
  thread: Thread; showSubject: boolean; isLast: boolean; onLoaded: () => void;
}) {
  const query = useApi<ThreadDetail>(`/admin/threads/${thread.id}`);
  const count = query.data?.messages.length ?? 0;

  // Recién cargado (o con mensaje nuevo), el último hilo lleva el scroll al
  // fondo — lo último dicho y el redactor quedan a la vista, como en cualquier
  // chat. Instantáneo, así `prefers-reduced-motion` no tiene nada que objetar.
  useEffect(() => {
    if (isLast && count > 0) onLoaded();
    // `onLoaded` queda deliberadamente fuera de las dependencias: solo mueve
    // el scroll, y meterla haría que el efecto se reejecute en cada render del
    // padre — el chat se iría al fondo mientras alguien lee hacia arriba.
  }, [isLast, count]);

  return (
    <Async query={query}>
      {({ thread: detail, messages }) => (
        <div className="chat-thread">
          {showSubject && (
            // El asunto histórico no se pierde en silencio (P6.3): queda como
            // separador entre los hilos de la misma conversación.
            <div className="chat-thread__divider">
              <span>{thread.subject}</span>
            </div>
          )}
          {messages.length === 0 ? (
            <p className="muted small chat-thread__empty">Todavía no hay mensajes.</p>
          ) : (
            messages.map((m) => {
              const fromClient = !m.is_internal
                && (m.author_id === null
                  || (detail.party_profile_id != null && m.author_id === detail.party_profile_id));
              const side = m.is_internal ? 'internal' : fromClient ? 'in' : 'out';
              return (
                <div key={m.id} className={`chat-msg chat-msg--${side}`}>
                  <div className="chat-msg__meta">
                    <span className="chat-msg__author">{m.author_name ?? 'Consultorio'}</span>
                    {Boolean(m.is_internal) && (
                      <Badge tone="warn" plain>nota interna — no la ve el consultorio</Badge>
                    )}
                  </div>
                  <p className="chat-msg__body">{m.body}</p>
                  <span className="chat-msg__date">{dateOnly(m.created_at)}</span>
                </div>
              );
            })
          )}
        </div>
      )}
    </Async>
  );
}

/* ── Nueva conversación ───────────────────────────────────────────────────── */

/**
 * Sin campo de asunto (P6.3: no existe más): consultorio, doctor y el primer
 * mensaje. El `subject` que el esquema exige se deriva del mensaje.
 */
function NewConversationModal({ onClose, onCreated }: {
  onClose: () => void; onCreated: () => void;
}) {
  const units = useApi<{ units: { id: string; unit_code: string; name: string | null }[] }>('/admin/units');
  const [unitId, setUnitId] = useState('');
  const [partyId, setPartyId] = useState('');
  const [body, setBody] = useState('');

  const doctors = useApi<{ doctors: { party_id: string; name: string }[] }>(
    unitId ? `/admin/units/${unitId}/doctors` : null,
  );

  const mutation = useApiMutation(
    () => api.post<{ id: string }>('/admin/threads', {
      service_unit_id: unitId,
      party_id: partyId || null,
      subject: subjectFromBody(body),
      body,
    }),
    ['/admin/threads'],
    { onSuccess: () => { haptic.success(); onCreated(); } }, // HAPTIC_FEEDBACK
  );

  return (
    <Modal
      title="Nueva conversación"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={!unitId || !body.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Enviando…' : 'Enviar'}
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      <Note tone="info">
        El mensaje no sale por WhatsApp ni SMS: queda acá y el consultorio lo
        lee cuando entra al portal.
      </Note>
      <div className="mt-2" />
      <Field label="Consultorio" required>
        <select
          className="select" value={unitId}
          onChange={(e) => { setUnitId(e.target.value); setPartyId(''); }}
        >
          <option value="">Elegí un consultorio…</option>
          {(units.data?.units ?? []).map((u) => (
            <option key={u.id} value={u.id}>{u.unit_code}{u.name ? ` · ${u.name}` : ''}</option>
          ))}
        </select>
      </Field>
      <Field label="Médico" hint="Cada doctor tiene su propia conversación; sin médico, la conversación es del consultorio.">
        <select
          className="select" value={partyId} disabled={!unitId}
          onChange={(e) => setPartyId(e.target.value)}
        >
          <option value="">Todo el consultorio</option>
          {(doctors.data?.doctors ?? []).map((d) => (
            <option key={d.party_id} value={d.party_id}>{d.name}</option>
          ))}
        </select>
      </Field>
      <Field label="Mensaje" required>
        <textarea className="input" rows={4} value={body} onChange={(e) => setBody(e.target.value)} autoFocus />
      </Field>
    </Modal>
  );
}
